import type { AllowedCourseItem } from '../../types';

const HTML_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", nbsp: ' ',
};

/**
 * The forum stores post bodies as HTML, so `body_preview` arrives as e.g.
 * `<p>Great module</p>`. It is learner-authored content and is rendered as
 * text, never as markup — hence stripping the tags here rather than injecting.
 */
export const plainTextPreview = (body: string): string => body
  .replace(/<[^>]*>/g, ' ')
  .replace(/&(#39|amp|lt|gt|quot|nbsp);/g, (_match, entity) => HTML_ENTITIES[entity] ?? ' ')
  .replace(/\s+/g, ' ')
  .trim();

/** Course-number segment of an opaque course id, used as the last-resort label. */
export const courseNumberFromId = (courseId: string): string => {
  const tail = courseId.split(':').pop() ?? courseId;
  const parts = tail.split(/[+/]/);
  return parts.length >= 2 ? parts[1] : courseId;
};

/**
 * Human-readable label for a learning-path course entry: the course display
 * name learners see, then configured title, then certificate display name,
 * then the short course-number as last resort.
 */
export const courseLabel = (
  course: Pick<AllowedCourseItem, 'course_id' | 'progress_badge_title' | 'certificate_display_name'>,
  displayName?: string,
): string => (
  displayName
    || course.progress_badge_title
    || course.certificate_display_name
    || courseNumberFromId(course.course_id)
);

const AVATAR_COLORS = [
  '#0a3887', '#1f6f54', '#8a4b1f', '#6b2f8a', '#0f6e7a', '#a8324a',
];

const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 1000000007;
  }
  return hash;
};

export const avatarColor = (key: string): string => (
  AVATAR_COLORS[hashString(key) % AVATAR_COLORS.length]
);

const PUNCTUATION = '()[]{}<>«»"\'`’.,;:!?/\\|-–—_+*&^%$#@~=';

export const initialsOf = (name: string): string => {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0 && !PUNCTUATION.includes(part[0]));
  if (parts.length === 0) {
    return '?';
  }
  const letters = parts.length === 1
    ? parts[0].slice(0, 2)
    : `${parts[0][0]}${parts[parts.length - 1][0]}`;
  return letters.toUpperCase();
};
